import { Link, useParams } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Plus } from "lucide-react";
import { useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";

import type { BankConnection } from "@/apis/gql/types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { TypographyLarge, TypographySmall } from "@/components/ui/typography";

import type { GetAllBankAccountsBankAccount } from "@/queries/bank-accounts/get-all";
import {
  BankConnectionCard,
  BankConnectionCardSkeleton,
} from "./bank-connection-card";
import { CreateBankConnectionForm } from "./create-bank-connection-form";
import { ManualBankAccountsSection } from "./manual-bank-accounts-section";

type DialogState = {
  open: boolean;
  step: number;
  error: boolean;
  abort: boolean;
  success: boolean;
};

type Props = {
  bankConnections: BankConnection[];
  bankAccounts: GetAllBankAccountsBankAccount[];
  loading: boolean;
  onDeleteBankConnection: (bankConnectionId: string) => void;
  onUpdateDefaultAccount: (
    accountId: string,
    isCurrentlyDefault: boolean,
  ) => void;
  onSetJournalAccount: (journalAccountId: string) => Promise<void>;
};

export const BankComponent = ({
  bankConnections,
  bankAccounts,
  loading,
  onDeleteBankConnection,
  onUpdateDefaultAccount,
  onSetJournalAccount,
}: Props) => {
  const { t } = useTranslation();
  const { company } = useParams({ strict: false });

  const [dialogState, setDialogState] = useQueryState(
    "bank-connection-dialog-state",
    {
      defaultValue: JSON.stringify({
        open: false,
        step: 0,
        error: false,
        abort: false,
        success: false,
        bank: null,
      }),
      parse: (value) => {
        try {
          return JSON.parse(value);
        } catch {
          return {
            open: false,
            step: 0,
            error: false,
            abort: false,
            success: false,
            bank: null,
          };
        }
      },
      serialize: (value) => JSON.stringify(value),
    },
  );

  const handleToggleDialog = () => {
    setDialogState((prev: DialogState) => ({
      ...prev,
      open: !prev.open,
      step: 0,
      error: false,
      abort: false,
      success: false,
      bank: null,
    }));
  };

  const handleSetStep = (step: number) => {
    setDialogState((prev: DialogState) => ({ ...prev, step }));
  };

  return (
    <>
      {dialogState.success && <BankConnectionSuccess />}
      {dialogState.error && <BankConnectionError />}
      {dialogState.abort && <BankConnectionAbort />}
      <Card>
        <CardHeader>
          <div className="flex justify-between">
            <div className="flex items-start gap-4">
              <div className="space-y-2">
                <TypographyLarge>
                  {t("bank_connections.empty_card.title")}
                </TypographyLarge>
                <TypographySmall className="text-muted-foreground">
                  {t("bank_connections.empty_card.description")}
                </TypographySmall>
              </div>
            </div>
            <Button onClick={handleToggleDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t("bank_connections.connect_bank")}
            </Button>
          </div>
        </CardHeader>
        <div className="flex flex-col gap-4 px-4 pb-4">
          {loading ? (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: not relevant here
                  <BankConnectionCardSkeleton key={index} />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4">
                {bankConnections.map((connection) => (
                  <BankConnectionCard
                    key={connection.bankConnectionId}
                    connection={connection}
                    onRemove={() =>
                      onDeleteBankConnection(connection.bankConnectionId)
                    }
                    onSetDefaultAccount={(accountId, isCurrentlyDefault) =>
                      onUpdateDefaultAccount(accountId, isCurrentlyDefault)
                    }
                    onSetJournalAccount={onSetJournalAccount}
                  />
                ))}
              </div>
            </div>
          )}
          <CreateBankConnectionForm
            open={dialogState.open}
            onCancel={handleToggleDialog}
            step={dialogState.step}
            error={dialogState.error}
            abort={dialogState.abort}
            success={dialogState.success}
            setStep={handleSetStep}
          />
        </div>
      </Card>
      <ManualBankAccountsSection
        bankAccounts={bankAccounts}
        onSetJournalAccount={onSetJournalAccount}
      />
      <Card className="mt-4">
        <CardHeader>
          <div className="space-y-2">
            <TypographyLarge>
              {t("transactions.file_import.title")}
            </TypographyLarge>
            <TypographySmall className="text-muted-foreground">
              {t("transactions.file_import.guidance_upload")}
            </TypographySmall>
          </div>
          <div className="flex justify-end mt-2">
            <Button asChild>
              <Link
                to="/$company/unternehmen/transaktionen/importieren"
                params={{ company: company as string }}
              >
                {t("transactions.file_import.title")}
              </Link>
            </Button>
          </div>
        </CardHeader>
      </Card>
    </>
  );
};

const BankConnectionSuccess = () => {
  const { t } = useTranslation();
  return (
    <Alert variant="success" className="mb-4">
      <CheckCircle className="h-4 w-4" />
      <AlertTitle>{t("bank_connections.success.title")}</AlertTitle>
      <AlertDescription className="whitespace-pre-line">
        {t("bank_connections.success.description")}
      </AlertDescription>
    </Alert>
  );
};

const BankConnectionError = () => {
  const { t } = useTranslation();
  return (
    <Alert variant="warning" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t("bank_connections.error.title")}</AlertTitle>
      <AlertDescription className="whitespace-pre-line">
        {t("bank_connections.error.description")}
      </AlertDescription>
    </Alert>
  );
};

const BankConnectionAbort = () => {
  const { t } = useTranslation();
  return (
    <Alert variant="warning" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t("bank_connections.abort.title")}</AlertTitle>
      <AlertDescription className="whitespace-pre-line">
        {t("bank_connections.abort.description")}
      </AlertDescription>
    </Alert>
  );
};
